import { useCallback, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import {
  fetchCurrentUser,
  login as apiLogin,
  loginWithOidc,
  logout as apiLogout,
} from '@/api/client'
import type { User } from '@/api/types'
import { AuthContext } from '@/auth/context'
import type { AuthState } from '@/auth/context'

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Restore the session on first render, if a token is present.
  useEffect(() => {
    let cancelled = false

    async function restore() {
      try {
        const current = await fetchCurrentUser()
        if (!cancelled) setUser(current)
      } catch {
        if (!cancelled) setUser(null)
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void restore()
    return () => {
      cancelled = true
    }
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await apiLogin({ email, password })
    setUser(result.user)
  }, [])

  const completeOidcSession = useCallback(async (idToken: string) => {
    const result = await loginWithOidc(idToken)
    setUser(result.user)
  }, [])

  const signOut = useCallback(async () => {
    await apiLogout()
    setUser(null)
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      isLoading,
      isAuthenticated: user !== null,
      signIn,
      completeOidcSession,
      signOut,
    }),
    [user, isLoading, signIn, completeOidcSession, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
